<template>
  <v-dialog :value="dialog" max-width="750" @input="close" scrollable>
    <v-card class="py-5">
      <CloseDialogButton :close="close" />
      <v-row no-gutters>
        <v-col class="pb-3 px-10">
          <div>
            {{ product.categories && product.categories[0].category_name }}
          </div>
          <div class="font-size-xl">{{ product.name }}</div>
        </v-col>
      </v-row>
      <v-row no-gutters v-if="!product.redeemable">
        <v-col class="pb-3 px-10">
          <div class="color-suva-grey">Starting at</div>
          <UnitPriceDisplay
            :byweight="product.byweight"
            :type="'generic'"
            :unitPrice="product.cents"
          />
        </v-col>
      </v-row>
      <v-row
        no-gutters
        v-if="
          !product.redeemable ||
          total_points_remaining - product.redemption_points_offer > -1 ||
          club_free_products_qty_remaining(product.club_id) > 0
        "
      >
        <v-col class="px-10">
          <v-btn
            color="primary"
            height="48"
            width="170"
            large
            rounded
            @click.stop="toProductDetail"
          >
            <div
              class="font-size-md font-weight-semibold text-capitalize text-center"
            >
              + add another
            </div>
          </v-btn>
        </v-col>
      </v-row>
      <v-row no-gutters class="border-bottom">
        <v-col cols="12">
          <div class="font-size-xl py-5 text-center">Already in Cart:</div>
        </v-col>
      </v-row>
      <div class="no-overflow" style="max-height: 600px">
        <v-row class="d-flex justify-center" no-gutters>
          <v-col
            class="border-top-not-first-child"
            cols="12"
            v-for="cartItem in cartProduct.cartItems"
            :key="cartItem.item_id"
          >
            <CartItem
              :close="close"
              :currentCartItem="true"
              :edit="edit"
              :items="items"
              :modifiedQty="getQuantityFromModifiedCartItems(cartItem.item_id)"
              :modifyCartItems="modifyCartItems"
              :product="cartItem"
              :upc="product.upc"
            >
              <template v-slot:quantityButton="{ displayQuantity, less, more }">
                <QuantityButton
                  :displayQuantity="displayQuantity"
                  :exceededMax="exceededMax()"
                  :less="less"
                  :more="more"
                />
              </template>
            </CartItem>
          </v-col>
        </v-row>
      </div>
      <v-row no-gutters class="border-top">
        <v-col class="pt-5 px-5 text-right">
          <v-btn
            color="#e21b23"
            height="48"
            width="141"
            rounded
            @click.stop="removeAll"
          >
            <div
              class="color-white font-size-md font-weight-semibold text-capitalize text-center"
            >
              remove all
            </div>
          </v-btn>
          <v-btn
            class="ml-5"
            color="success"
            height="48"
            width="141"
            rounded
            @click.stop="done"
          >
            <div
              class="font-size-md font-weight-semibold text-capitalize text-center"
            >
              done
            </div>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
let watch = true;
let nonReactiveCart = [];

function _initNonReactiveCart(cartProducts) {
  nonReactiveCart = cartProducts;
}

function _setWatchToFalse() {
  watch = false;
}

function _setCartWithModifiedCartItemsForNewPricing(cartItem, cartProducts) {
  const { item_id, qty } = cartItem;
  if (qty === 0) {
    nonReactiveCart = cartProducts.filter(
      (product) => product.item_id !== item_id,
    );
  } else {
    nonReactiveCart = updateObjectInArray(cartProducts, cartItem, "item_id");
  }
}

import { mapGetters, mapState } from "vuex";
import {
  getProductDetailViewFromProductState,
  updateObjectInArray,
  useSharedLibCart,
} from "@/util.js";
import { calculateCart } from "@/worker-api.js";
import CartItem from "@/components/Product/CartItem.vue";
import CloseDialogButton from "@/components/UI/CloseDialogButton.vue";
import QuantityButton from "@/components/Product/QuantityButton.vue";
import UnitPriceDisplay from "@/components/Product/UnitPriceDisplay.vue";
import {DriverTips, StoreTips } from '@/types/tips.js';

export default {
  name: "ProductAddFromCartDialog",
  components: {
    CartItem,
    CloseDialogButton,
    QuantityButton,
    UnitPriceDisplay,
  },
  computed: {
    ...mapGetters({
      getProductItemsAndQuantityInCart: "cart/getProductItemsAndQuantityInCart",
    }),
    ...mapGetters("store", {
      total_points_remaining: "total_points_remaining",
      club_free_products_qty_remaining: "club_free_products_qty_remaining",
    }),
    ...mapState({
      cart: (state) => state.cart,
      dialog: (state) => state.product.dialog.addFromCart,
      product: (state) => state.product.details,
      sharedLibPromos: (state) => state.promotions.sharedLibPromos,
      storeInfo: (state) => state.store.info,
    }),
    cartProduct() {
      const itemsAndQuantityFromCart = this.getProductItemsAndQuantityInCart(
        this.product,
      );
      return { ...this.product, ...itemsAndQuantityFromCart };
    },
  },
  data() {
    return {
      items: null,
      modifiedCartItems: [],
    };
  },
  methods: {
    close() {
      this.modifiedCartItems = [];
      this.$store.commit("product/CLOSE_DIALOG", "addFromCart");
    },
    done() {
      if (this.modifiedCartItems.length) {
        this.$store.dispatch("cart/updateCartItems", this.modifiedCartItems);
      }
      this.close();
    },
    edit(product) {
      this.close();
      this.$store.dispatch("product/editModifier", product);
      this.$store.dispatch("product/openDialog", "modifiers");
    },
    exceededMax() {
      let qty = this.getCurrentQuantity();
      return this.product.redeemable
        ? this.product.is_redemption
          ? qty == this.getClubTotal()
          : this.total_points_remaining -
              (qty - this.product.cartQty + 1) *
                this.product.redemption_points_offer <
            0
        : false;
    },
    getClubTotal() {
      let clubTotal = this.storeInfo.coffee_clubs?.totals?.find(
        (t) => t.club_id === this.product.club_id,
      );
      return clubTotal?.free_products_qty;
    },
    getCurrentQuantity() {
      let products;
      if (this.modifiedCartItems.length) {
        var a = this.cart.products.map((i) => {
          let item = this.modifiedCartItems.find((m) => m.item_id == i.item_id);
          return item || i;
        });
        products = a;
      } else {
        products = this.cart.products;
      }
      let qty = products.reduce(
        (t, i) =>
          t +
          ((this.product.redemption_points_offer_id &&
            this.product.redemption_points_offer_id ===
              i.redemption_points_offer_id) ||
          (this.product.club_id && this.product.club_id === i.club_id)
            ? i.qty
            : 0),
        0,
      );
      return qty;
    },
    getQuantityFromModifiedCartItems(itemId) {
      if (!this.modifiedCartItems.length) return null;
      const mod = this.modifiedCartItems.find(
        (modifiedCartItem) => modifiedCartItem.item_id === itemId,
      );
      return mod ? mod.qty : null;
    },
    modifyCartItems(cartItem) {
      const has =
        this.modifiedCartItems.length &&
        this.modifiedCartItems.some((_) => _.item_id === cartItem.item_id);
      this.modifiedCartItems = has
        ? updateObjectInArray(this.modifiedCartItems, cartItem, "item_id")
        : [...this.modifiedCartItems, cartItem];
      _setCartWithModifiedCartItemsForNewPricing(cartItem, this.cart.products);
      this.setPromotionsPricing();
    },
    async removeAll() {
      this.$store.dispatch("cart/removeAllCartItems", this.cartProduct);
      this.$store.commit("product/REMOVE_ALL_CART_ITEMS");
      this.close();
    },
    async setPromotionsPricing() {
      const deliveryAmount = null; // not relevant to cart calculation until checkout

      const sharedLibCart = useSharedLibCart(
        nonReactiveCart,
        this.sharedLibPromos,
        this.storeInfo.modifierGroups,
        deliveryAmount,
        this.cart.firstOrderDiscount,
        this.storeInfo.fee_buster_config,
        DriverTips.other,
        StoreTips.other,
        this.storeInfo.points_config,
        this.storeInfo.price_increase,
      );
      const { items } = await calculateCart(sharedLibCart);
      this.items = items;
    },
    toProductDetail() {
      this.$store.dispatch(
        "product/setCurrentProductDetails",
        this.cartProduct,
      );
      this.$store.dispatch("product/openDialog", "modifiers");
      this.close();
    },
  },
  watch: {
    "$store.state.cart.products": function () {
      _initNonReactiveCart(this.cart.products);
      this.sharedLibPromos &&
        this.$vuetify.breakpoint.xsOnly &&
        getProductDetailViewFromProductState(this.product) !== "addFromCart" &&
        this.$route.name !== "cart" &&
        this.setPromotionsPricing() &&
        watch &&
        _setWatchToFalse();
    },
    "$store.state.promotions.sharedLibPromos": function () {
      watch &&
        this.cart?.products?.length &&
        _initNonReactiveCart(this.cart.products) &&
        this.setPromotionsPricing() &&
        _setWatchToFalse();
    },
    "$store.state.product.dialog.addFromCart": function (value) {
      value && this.$vuetify.breakpoint.smAndUp && this.setPromotionsPricing();
    },
  },
};
</script>
